import { filterNonNullable } from '@lib/filters';

import {
  getComparisonKey,
  serializeAlert,
  uniqueAlerts,
  unserializeAlert,
} from './serialize';

import type { AlertStorage } from './types';

const STORAGE_KEY = 'hiddenAlerts';

export const createAlertClientStorage = (): AlertStorage => {
  return {
    get,
    save,
    delete: deleteFunc,
    flush,
  };
};

const get: AlertStorage['get'] = async () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }

  try {
    const json = window.localStorage.getItem(STORAGE_KEY);

    if (json === null) {
      return [];
    }

    const serialized = JSON.parse(json);

    if (!Array.isArray(serialized)) {
      return [];
    }

    const filtered = serialized.filter((alert): alert is string => {
      return typeof alert === 'string';
    });

    return filtered.map(unserializeAlert).filter(filterNonNullable);
  } catch (e) {
    return [];
  }
};

const save: AlertStorage['save'] = async alertArg => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  const alerts = Array.isArray(alertArg) ? alertArg : [alertArg];

  if (alerts.length === 0) {
    return true;
  }

  const current = await get();
  const updated = uniqueAlerts([...current, ...alerts]);
  const serialized = updated.map(serializeAlert);

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));

  return true;
};

const deleteFunc: AlertStorage['delete'] = async alertsArg => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  const alerts = Array.isArray(alertsArg) ? alertsArg : [alertsArg];

  const keysToDelete = alerts.map(alert => getComparisonKey(alert));

  const current = await get();
  const updated = current.filter(alert => {
    return !keysToDelete.includes(getComparisonKey(alert));
  });
  const serialized = updated.map(serializeAlert);

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));

  return true;
};

const flush: AlertStorage['flush'] = async () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  window.localStorage.removeItem(STORAGE_KEY);

  return true;
};
